import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import OptimizedImage from '../../components/OptimizedImage';

import SEO from '../../components/SEO';

const ZoneAnnecy = () => {
  const cities = [
    'Annecy', 'Annecy-le-Vieux', 'Cran-Gevrier', 'Seynod', 'Meythet', 'Poisy', 
    'Argonay', 'Epagny-Metz-Tessy', 'Villaz', 'Sevrier', 'Saint-Jorioz', 
    'Veyrier-du-Lac', 'Menthon-Saint-Bernard', 'Talloires-Montmin', 'Duingt', 
    'Rumilly', 'Thônes', 'Frangy'
  ];

  const services = [
    {
      title: 'Débarras de maison',
      description: 'Vide-maison complet pour les propriétés du bassin annécien et du pourtour du lac.'
    },
    {
      title: 'Débarras d\'appartement',
      description: 'Intervention dans tous les quartiers d\'Annecy, même sans ascenseur.'
    },
    {
      title: 'Débarras cave et grenier',
      description: 'Désencombrement des espaces de stockage des maisons et résidences du secteur.'
    },
    {
      title: 'Débarras succession',
      description: 'Accompagnement respectueux pour les familles annéciennes.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Débarras Annecy et bassin annécien | DEBECO"
        description="Service de débarras professionnel à Annecy, Seynod, Cran-Gevrier et tout le bassin annécien. Intervention rapide sous 48-72h."
        canonical="/zones/annecy"
        ogImage="/images/annecy.jpg"
        jsonLd={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Accueil","item":"https://debeco.fr/"},{"@type":"ListItem","position":2,"name":"Zones d'intervention","item":"https://debeco.fr/zones"},{"@type":"ListItem","position":3,"name":"Annecy et bassin annécien"}]}}
      />
      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Débarras à Annecy et bassin annécien</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Service de débarras mobilier dans tout le bassin annécien
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              {/* Image */}
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="/images/annecy.jpg"
                  webpSrc="/images/annecy.webp"
                  alt="Vue panoramique du lac d'Annecy entouré de montagnes, zone d'intervention DEBECO"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="800"
                  loading="eager"
                  data-testid="zone-annecy-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <MapPin className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Votre partenaire débarras à Annecy</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  DEBECO intervient sur Annecy et l'ensemble du bassin annécien ainsi que le pourtour du lac d'Annecy. Que vous habitiez dans la vieille ville, dans les quartiers résidentiels ou au bord du lac, notre équipe se déplace rapidement pour vos besoins de débarras.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Nous connaissons parfaitement le secteur et ses spécificités : immeubles anciens du centre-ville, résidences des communes voisines, maisons à proximité du lac d'Annecy... Notre expérience locale nous permet d'intervenir efficacement partout.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/devis">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Estimer le coût
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Cities */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Villes couvertes</h2>
              <div className="flex flex-wrap justify-center gap-3">
                {cities.map((city, index) => (
                  <span key={index} className="bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-medium">
                    {city}
                  </span>
                ))}
              </div>
            </div>

            {/* Services avec pages locales */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Nos services à Annecy</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Link to="/debarras-maison-annecy" className="group">
                  <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3 group-hover:text-emerald-700 transition-colors flex items-center">
                        Débarras de maison
                        <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                      <p className="text-gray-600">Vide-maison complet pour les propriétés du bassin annécien, de la Vieille Ville aux hauteurs du Parmelan.</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/debarras-appartement-annecy" className="group">
                  <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3 group-hover:text-emerald-700 transition-colors flex items-center">
                        Débarras d'appartement
                        <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                      <p className="text-gray-600">Intervention dans tous les immeubles d'Annecy, même sans ascenseur. Protection des parties communes garantie.</p>
                    </CardContent>
                  </Card>
                </Link>
                {[
                  { title: 'Débarras cave et grenier', description: "Désencombrement des espaces de stockage des maisons et résidences du secteur." }
                ].map((service, index) => (
                  <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600">{service.description}</p>
                    </CardContent>
                  </Card>
                ))}
                <Link to="/debarras-succession-annecy" className="group">
                  <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3 group-hover:text-emerald-700 transition-colors flex items-center">
                        Débarras succession
                        <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                      <p className="text-gray-600">Accompagnement respectueux pour les familles annéciennes, coordination avec les notaires du bassin.</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/vide-maison-annecy" className="group">
                  <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3 group-hover:text-emerald-700 transition-colors flex items-center">
                        Vide-maison
                        <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                      <p className="text-gray-600">Vidage intégral de votre maison avant vente, déménagement ou travaux, des Fins à Albigny.</p>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { number: '48-72h', label: 'Délai d\'intervention' },
                { number: '18+', label: 'Communes couvertes' },
                { number: '100%', label: 'Clients satisfaits' },
                { number: '7j/7', label: 'Disponibilité' }
              ].map((stat, index) => (
                <Card key={index} className="text-center border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                  <CardContent className="p-6">
                    <p className="text-4xl font-bold text-blue-900 mb-2">{stat.number}</p>
                    <p className="text-gray-600">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Nos services à Annecy</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/debarras-maison-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras de maison à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Service spécialisé quartier par quartier</p>
            </Link>
            <Link to="/debarras-appartement-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras d'appartement à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Même sans ascenseur, intervention soignée</p>
            </Link>
            <Link to="/debarras-succession-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras succession à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Accompagnement respectueux, coordination notaire</p>
            </Link>
            <Link to="/vide-maison-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Vide-maison à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Vidage intégral avant vente, déménagement ou travaux</p>
            </Link>
            <Link to="/blog/prix-debarras-maison-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Guide des prix de débarras à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Fourchettes tarifaires détaillées pour 2026</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras à Annecy ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit et une intervention rapide dans tout le bassin annécien
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ZoneAnnecy;
